import {
  Grid,
  Typography,
  Box,
  FormControl,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  OutlinedInput,
  DialogTitle,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Stack,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import TextField from "@mui/material/TextField";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import * as services from "./../../../services/saleng";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BackspaceIcon from "@mui/icons-material/Backspace";
import Keypad from "./../../../components/Keypad";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Webcam from "react-webcam";
import { SALENG_SHOP_API_PROD } from "../../../config";
import { formatNumber } from "../../../utils/formatNumber";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PriceSelectionDialog from "./PriceSelectionDialog";
import { useProductsBranch } from "../../../hooks/useProductsBranch";

const SALENG_SHOP_API = SALENG_SHOP_API_PROD;
const beep = new Audio("/sounds/beep.mp3");

/* ----------------------- ProductSelectDialog ----------------------- */
const ProductSelectDialog = ({
  open,
  onClose,
  onConfirm,
  products,
  categories,
  initialSelectedId,
}) => {
  const theme = useTheme();
  const [catFilter, setCatFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);

  useEffect(() => {
    if (open) {
      setCatFilter(null);
      setSearchQuery("");
      setSelectedId(initialSelectedId || null);
    }
  }, [open, initialSelectedId]);

  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const catColorMap = useMemo(() => {
    return categories.reduce((m, c, i) => {
      m[c.name] = colorPalette[i % colorPalette.length];
      return m;
    }, {});
  }, [categories]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (catFilter && p.category.name !== catFilter) return false;
      if (
        searchQuery &&
        !p.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;
      return true;
    });
  }, [products, catFilter, searchQuery]);

  const toggleSelect = (id) =>
    setSelectedId((prev) => (prev === id ? null : id));

  const confirm = () => {
    const prod = products.find((p) => p.id === selectedId);
    if (prod) onConfirm(prod);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>เลือกสินค้า</DialogTitle>
      <DialogContent sx={{ display: "flex", p: 2 }}>
        {/* Sidebar หมวด */}
        <Paper
          variant="outlined"
          sx={{ width: 240, maxHeight: "70vh", overflowY: "auto", mr: 2 }}
        >
          <Box
            onClick={() => setCatFilter(null)}
            sx={{
              p: 2,
              cursor: "pointer",
              bgcolor: catFilter === null ? "action.selected" : "transparent",
            }}
          >
            ทั้งหมด
          </Box>
          {[...new Map(categories.map((cat) => [cat.name, cat])).values()].map(
            (cat) => (
              <Box
                key={cat.id}
                onClick={() => setCatFilter(cat.name)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  bgcolor:
                    catFilter === cat.name ? "action.selected" : "transparent",
                }}
              >
                {cat.name}
              </Box>
            )
          )}
        </Paper>

        {/* Main เลือกสินค้า */}
        <Box flex={1} display="flex" flexDirection="column">
          <TextField
            fullWidth
            placeholder="ค้นหาชื่อสินค้า..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box flex={1} overflow="auto">
            <Grid container spacing={2}>
              {filtered.map((p) => {
                const isSel = selectedId === p.id;
                return (
                  <Grid item xs={6} sm={4} md={3} key={p.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        borderColor: isSel ? "success.main" : undefined,
                        bgcolor: isSel
                          ? theme.palette.grey[300]
                          : alpha(
                              catColorMap[p.category.name] || grey[200],
                              0.2
                            ),
                      }}
                    >
                      <CardActionArea onClick={() => toggleSelect(p.id)}>
                        <CardContent>
                          <Typography noWrap>{p.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.subCategory?.name}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Chip
          label={`เลือกแล้ว ${selectedId ? 1 : 0} ชิ้น`}
          color={selectedId ? "success" : "default"}
        />
        <Button size="large" onClick={() => setSelectedId(null)}>
          ล้าง
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={confirm}
          disabled={!selectedId}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};
/* --------------------- /ProductSelectDialog --------------------- */

const SimpleDialog = (props) => {
  const theme = useTheme();
  const {
    open,
    onClose,
    onSubmit,
    selectedValue,
    customer: initialCustomer,
  } = props;

  // ---------------------- state ----------------------
  const [weight, setWeight] = useState(0);
  const [deduct, setDeduct] = useState(0);
  const [focusedImpurityIndex, setFocusedImpurityIndex] = useState(null);
  const [focusedImpurityField, setFocusedImpurityField] = useState(null);
  const [productPrice, setProductPrice] = useState(0);
  const [mute, setMute] = useState(false);
  const [focused, setFocused] = useState("weight"); // "weight" | "deduct" | null
  const [openPriceDialog, setOpenPriceDialog] = useState(false);

  const lastWeightRef = useRef(null);
  const stableStartTimeRef = useRef(null);
  const beepPlayedRef = useRef(false);

  // เลือกสินค้าอื่นเป็นสิ่งเจือปน
  const [openProdDialog, setOpenProdDialog] = useState(false);
  const [catFilter, setCatFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  const [impurities, setImpurities] = useState([{ type: "", weight: 0 }]);
  const [deductedItems, setDeductedItems] = useState([{ type: "", weight: 0 }]);
  const impurityOptions = [
    "ลังเหล็ก",
    // "ปุ๋ย",
    // "ลัง",
    // "เต๋า",
    // "ขยะ+ฝุ่นดิน",
    // "เหล็กทิ้ง",
    // "น้ำ",
    // "พลาสติก",
    // "สติกเกอร์",
    // "กระบะ",
    // "พาเลท",
    // "ถ่าน",
    // "สังกะสี",
    "อื่นๆ",
  ];

  const webcamRef = useRef(null);
  const [inputValue, setInputValue] = useState(0);
  const { products } = useProductsBranch();
  const [openMainProdDialog, setOpenMainProdDialog] = useState(false);
  const [mainProduct, setMainProduct] = useState(null);
  const [prodPickerIndex, setProdPickerIndex] = useState(null);
  const [note, setNote] = useState("");

  // ✅ โหมดแก้ไขหมายเหตุ → ล็อก Keypad
  const [isEditingNote, setIsEditingNote] = useState(false);

  // ---------------------- helpers ----------------------
  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => {
        if (!cat) return false;
        if (seen.has(cat.name)) return false;
        seen.add(cat.name);
        return true;
      });
  }, [products]);

  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];

  const catColorMap = useMemo(() => {
    return categories.reduce((m, c, i) => {
      m[c.name] = colorPalette[i % colorPalette.length];
      return m;
    }, {});
  }, [categories, colorPalette]);

  const selectedProductIds = useMemo(() => {
    return [
      ...impurities
        .filter((imp) => imp.type === "__deduct_product__")
        .map((imp) => imp.productId),
      ...(selectedValue?.deductedItems?.map((item) => item.productId) || []),
    ];
  }, [impurities, selectedValue]);

  const filteredProds = products.filter((p) => {
    if (catFilter && p.category.name !== catFilter) return false;
    if (
      searchQuery &&
      !p.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    if (selectedProductIds.includes(p.id)) return false; // ห้ามซ้ำ
    return true;
  });

  // sync mainProduct และ note เมื่อเปิด dialog หรือ selectedValue เปลี่ยน
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devs) => {
      const videoInputs = devs.filter((d) => d.kind === "videoinput");
      setDevices(videoInputs);
      if (videoInputs.length > 0) {
        setSelectedDeviceId(videoInputs[0].deviceId); // default
      }
    });
    if (selectedValue?.id && selectedValue?.title) {
      setMainProduct({
        id: selectedValue.id,
        title: selectedValue.title,
        categoryId: selectedValue.categoryId,
      });
    } else {
      setMainProduct(null);
    }
    setNote(selectedValue?.note ?? "");
  }, [selectedValue]);

  // ------------- effects & data fetching -------------
  useEffect(() => {
    if (selectedValue) {
      const {
        weight: w = 0,
        deduct: d = 0,
        impurities: imps = [],
        deductedItems: deds = [],
      } = selectedValue;

      setWeight(w);
      setDeduct(d);
      setImpurities(imps);
      setDeductedItems(deds);
    }

    const socket = new WebSocket("ws://localhost:3001/ws");
    socket.onmessage = handleData;

    const interval = setInterval(() => {
      if (lastWeightRef.current !== null) {
        const elapsed = Date.now() - stableStartTimeRef.current;
        if (
          elapsed >= 2500 &&
          !beepPlayedRef.current &&
          lastWeightRef.current !== 0 &&
          !mute
        ) {
          // beep.play().catch(() => {});
          beepPlayedRef.current = true;
        }
      }
    }, 200);

    if (!open) {
      // reset เมื่อปิด dialog
      setWeight(0);
      setDeduct(0);
      setImpurities([{ type: "", weight: 0 }]);
      setFocused("weight");
      setFocusedImpurityIndex(null);
      setFocusedImpurityField(null);
      setInputValue(0);
      setNote("");
      setIsEditingNote(false);
    }

    return () => {
      socket.close();
      clearInterval(interval);
    };
  }, [selectedValue, open, mute]);

  useEffect(() => {
    if (!initialCustomer) return;
    const pid = mainProduct?.id || selectedValue?.id;
    if (!pid) return;

    const fetchProductPrice = async () => {
      try {
        const payload = { customerId: initialCustomer.id, productId: pid };
        const response = await services.getPriceProduct(payload);
        const fetchedPrice = response?.data?.data || {};

        let updatedPrice = {
          ...fetchedPrice,
          adjPrice:
            (fetchedPrice.basePrice ?? 0) + (fetchedPrice.groupAdjustment ?? 0),
        };

        const isSameProduct =
          !mainProduct?.id || mainProduct.id === selectedValue?.id;
        if (isSameProduct && selectedValue?.finalPrice != null) {
          updatedPrice.adjPrice = parseFloat(selectedValue.finalPrice) || 0;
        }

        setProductPrice(updatedPrice);
      } catch (error) {
        console.error("Error fetching product price:", error);
        setProductPrice(null);
      }
    };

    fetchProductPrice();
  }, [initialCustomer, selectedValue, mainProduct?.id]);

  // ---------- คำนวณสุทธิ ----------
  const calculateNetWeight = (w, d, i) => {
    const weightNum = parseFloat(w || 0);
    const deductNum = parseFloat(d || 0);
    const impuritiesNum = parseFloat(i || 0);
    return weightNum - deductNum - impuritiesNum;
  };

  const totalImpurityWeight = useMemo(() => {
    const normalImpuritiesWeight = impurities.reduce(
      (sum, item) => sum + parseFloat(item.weight || 0),
      0
    );
    const deductedItemsWeight = deductedItems.reduce(
      (sum, item) => sum + parseFloat(item.weight || 0),
      0
    );
    return normalImpuritiesWeight + deductedItemsWeight;
  }, [impurities, deductedItems]);

  const netWeight = useMemo(() => {
    return calculateNetWeight(weight, deduct, totalImpurityWeight);
  }, [weight, deduct, totalImpurityWeight]);

  const isNetNegative = netWeight < 0;
  const canSubmit = Number(weight) > 0 && netWeight > 0 && !isNetNegative;

  const display2 = (val) =>
    val === "" || val == null || Number.isNaN(val)
      ? "0.00"
      : Number(val).toFixed(2);

  const sanitizeNumber = (s) => {
    if (s == null) return "";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, "");
    return x;
  };

  // ---------- sync keypad ----------
  useEffect(() => {
    if (focused === null) return;
    setInputValue(focused === "weight" ? weight || 0 : deduct || 0);
  }, [focused, weight, deduct]);

  // ========== Keyboard handler ==========
  useEffect(() => {
    if (!open) return;

    const isTypingInTextInput = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const isInputLike =
        tag === "input" ||
        tag === "textarea" ||
        el.getAttribute?.("contenteditable") === "true";
      return isInputLike;
    };

    const commitLive = (nextStr) => {
      const v = sanitizeNumber(nextStr);
      setInputValue(v === "" ? 0 : v);

      if (isEditingNote) return; // ✅ กำลังพิมพ์หมายเหตุ → ไม่ยุ่งเลข

      if (focused === "weight") {
        setWeight(v);
      } else if (focused === "deduct") {
        setDeduct(v);
      } else if (
        focusedImpurityField === "weight" &&
        focusedImpurityIndex !== null
      ) {
        setImpurities((prev) => {
          const arr = [...prev];
          if (!arr[focusedImpurityIndex]) return prev;
          arr[focusedImpurityIndex] = {
            ...arr[focusedImpurityIndex],
            weight: v,
          };
          return arr;
        });
      }
    };

    const onKeyDown = (e) => {
      if (isEditingNote) return;

      if (openProdDialog || openMainProdDialog || openPriceDialog) return;
      if (isTypingInTextInput()) return;

      const editing =
        focused === "weight" ||
        focused === "deduct" ||
        focusedImpurityIndex !== null;
      if (!editing) return;

      const key = e.key;

      if ((key >= 0 && key <= "9") || key === ".") {
        e.preventDefault();
        const cur = String(inputValue ?? 0);
        if (key === "." && cur.includes(".")) return;
        const next = cur === 0 && key !== "." ? key : (cur || "") + String(key);
        commitLive(next);
        return;
      }

      if (key === "Backspace") {
        e.preventDefault();
        const cur = String(inputValue ?? 0);
        const next = cur.length <= 1 ? 0 : cur.slice(0, -1);
        commitLive(next);
        return;
      }

      if (key === "Escape") {
        e.preventDefault();
        commitLive(0);
        return;
      }

      if (key === "Enter") {
        e.preventDefault();
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    inputValue,
    focused,
    focusedImpurityField,
    focusedImpurityIndex,
    openProdDialog,
    openMainProdDialog,
    openPriceDialog,
    isEditingNote, // ✅
  ]);

  const handleData = (event) => {
    const data = JSON.parse(event.data);
    if (data.weight != null) {
      const rawWeight = Number(data.weight);
      const roundedWeight = parseFloat(rawWeight.toFixed(3));
      if (
        lastWeightRef.current === null ||
        Math.abs(roundedWeight - lastWeightRef.current) > 0.05
      ) {
        lastWeightRef.current = roundedWeight;
        stableStartTimeRef.current = Date.now();
        beepPlayedRef.current = false;
      }
    }
  };

  const captureImage = useCallback(async () => {
    if (!webcamRef.current) return null;
    const video = webcamRef.current.video;
    if (!video || video.readyState < 2) {
      console.warn("No webcam video available");
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL("image/jpeg", 1);
    if (!imageSrc) return null;
    const blob = await (await fetch(imageSrc)).blob();
    const file = new File([blob], "webcam_photo.jpg", { type: "image/jpeg" });
    return file;
  }, []);

  async function uploadFile(file) {
    if (!file) throw new Error("No file to upload");
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(SALENG_SHOP_API + "/files", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) throw new Error("Upload failed: " + response.statusText);
    const result = await response.json();
    return result;
  }

  const handleSummit = async () => {
    if (!(Number(weight) > 0)) {
      alert("กรุณากรอกน้ำหนักรวมให้มากกว่า 0");
      return;
    }
    if (!(netWeight > 0)) {
      alert("น้ำหนักสุทธิต้องมากกว่า 0");
      return;
    }

    let imageUrl = null;
    try {
      const capturedImage = await captureImage();
      if (capturedImage) {
        const uploadResult = await uploadFile(capturedImage);
        imageUrl = uploadResult?.file ?? imageUrl;
      }
    } catch (err) {
      console.log("ถ่าย/อัปโหลดรูปไม่สำเร็จ จะดำเนินการต่อโดยไม่แนบรูป:", err);
    }

    try {
      const normalImpurities = impurities.filter(
        (imp) => imp.type !== "__deduct_product__"
      );
      const deductedItemImpurities = impurities.filter(
        (imp) => imp.type === "__deduct_product__"
      );
      const dedItems = deductedItemImpurities
        .map((imp) => {
          const prod = products.find((p) => p.id === imp.productId);
          if (!prod) return null;
          return { ...prod, weight: parseFloat(imp.weight || 0) };
        })
        .filter(Boolean);

      // ใช้ product ปัจจุบันจาก dialog หากมี
      const productId = mainProduct?.id || selectedValue?.id;
      const productTitle = mainProduct?.title || selectedValue?.title;
      const categoryId = mainProduct?.categoryId ?? selectedValue?.categoryId;

      // คำนวณสุทธิ ณ เวลาส่ง
      const normalImpuritiesWeight = normalImpurities.reduce(
        (s, it) => s + parseFloat(it.weight || 0),
        0
      );
      const dedItemsWeight = dedItems.reduce(
        (s, it) => s + parseFloat(it.weight || 0),
        0
      );
      const netFromSubmit =
        parseFloat(weight || 0) -
        parseFloat(deduct || 0) -
        (normalImpuritiesWeight + dedItemsWeight);

      onSubmit({
        ...selectedValue,
        id: productId,
        title: productTitle,
        categoryId,
        finalPrice: productPrice?.adjPrice,
        weight,
        deduct,
        netWeight: netFromSubmit,
        impurities: normalImpurities,
        deductedItems: dedItems,
        imageUrl,
        productPrice,
        note,
      });

      // reset ฟอร์มที่จำเป็น
      setWeight("");
      setDeduct("");
      setNote("");
      setImpurities([{ type: "", weight: 0 }]);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการส่งข้อมูล:", error);
    }
  };

  const handleImpurityChange = (index, field, value) => {
    const updated = [...impurities];
    updated[index][field] = value;
    setImpurities(updated);
  };

  const addImpurity = () =>
    setImpurities([...impurities, { type: "", weight: 0 }]);
  const removeImpurity = (index) =>
    setImpurities(impurities.filter((_, i) => i !== index));

  const isDeductProductFromAPI = (item) => {
    if (item.type !== "__deduct_product__") return false;
    return selectedValue?.deductedItems?.some(
      (d) => d.productId === item.productId
    );
  };

  const handleSelectPrice = (newPrice) => {
    setProductPrice((prev) => ({
      ...prev,
      adjPrice: newPrice.adjPrice,
    }));
    setOpenPriceDialog(false);
  };

  // ---------------------- render ----------------------
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          รายการสินค้า
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Grid container spacing={4} mb={5}>
\            <Grid item xs={12} md={12} lg={4} sx={{ display: "flex" }}>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  flex: 1,
                  display: "flex",
                  backgroundColor: "black",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative", // 👈 ต้องมี
                }}
              >
                <Webcam
                  ref={webcamRef}
                  key={selectedDeviceId}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={1}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  videoConstraints={{
                    width: 1920,
                    height: 1080,
                    deviceId: selectedDeviceId
                      ? { exact: selectedDeviceId }
                      : undefined,
                  }}
                />

                {/* Select ทับบนกล้อง */}
                <Select
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(255,255,255,0.9)", 
                    borderRadius: 1,
                    minWidth: 100,
                  }}
                >
                  {devices.map((d) => (
                    <MenuItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `กล้อง ${d.deviceId}`}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <Stack spacing={2} sx={{ pr: { md: 2 } }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      สินค้า
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {mainProduct?.title || selectedValue?.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setOpenMainProdDialog(true)}
                        title="แก้ไขสินค้า"
                        sx={{ bgcolor: "action.hover" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      onClick={() => setOpenPriceDialog(true)}
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        "&:hover": { bgcolor: "primary.dark" },
                      }}
                      title="เลือกราคา"
                    >
                      <FormatListBulletedIcon />
                    </IconButton>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: 20, fontWeight: 700 }}
                    >
                      {formatNumber(productPrice?.adjPrice)} บาท/กก.
                    </Typography>
                  </Box>
                </Box>

                {/* น้ำหนัก รวม / หัก */}
                <Grid container>
                  <Grid item xs={12} md={12}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      รวม
                    </Typography>
                    <TextField
                      fullWidth
                      value={weight}
                      onFocus={() => {
                        setFocused("weight");
                        setIsEditingNote(false); // ถ้าเคยโฟกัส note มาก่อน
                        setInputValue(weight || 0);
                      }}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          fontWeight: 700,
                          fontSize: 24,
                          bgcolor:
                            focused === "weight"
                              ? "action.hover"
                              : "background.paper",
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography color="text.secondary">กก.</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={12} mt={1}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      หัก
                    </Typography>
                    <TextField
                      fullWidth
                      value={deduct}
                      onFocus={() => {
                        setFocused("deduct");
                        setIsEditingNote(false);
                        setInputValue(deduct || 0);
                      }}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          fontWeight: 700,
                          fontSize: 24,
                          bgcolor:
                            focused === "deduct"
                              ? "action.hover"
                              : "background.paper",
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography color="text.secondary">กก.</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* สิ่งเจือปน */}
                <Grid container>
                  <Grid item xs={12} md={12}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          สิ่งเจือปน
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "action.hover" }}
                          onClick={addImpurity}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Box>

                      {[
                        ...impurities,
                        ...deductedItems.map((item) => ({
                          type: "__deduct_product__",
                          weight: item.weight,
                          productId: item.productId,
                          productName: item.productName,
                          isDeductProduct: true,
                        })),
                      ].map((item, index) => {
                        const selectedProduct = products.find(
                          (p) => p.id === item.productId
                        );
                        const disabled = isDeductProductFromAPI(item);

                        return (
                          <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            key={`${item.type}-${index}`}
                            sx={{ my: 1 }}
                          >
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <Select
                                  value={item.type}
                                  displayEmpty
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleImpurityChange(index, "type", newValue);
                                    if (newValue === "ลังเหล็ก") {
                                      handleImpurityChange(index, "weight", 65.6);
                                    }
                                  }}
                                
                                  sx={{ fontSize: 20 }}
                                  input={<OutlinedInput />}
                                  renderValue={(selected) => {
                                    if (selected === "__deduct_product__") {
                                      return selectedProduct
                                        ? `หักเป็น${selectedProduct.title}`
                                        : item.productName
                                          ? `หักเป็น${item.productName}`
                                          : "หักเป็นสินค้าอื่น";
                                    }
                                    return selected || "เลือกสิ่งเจือปน";
                                  }}
                                  disabled={disabled}
                                >
                                  <MenuItem value="" disabled>
                                    เลือกสิ่งเจือปน
                                  </MenuItem>
                                  {/* <MenuItem
                                    value="__deduct_product__"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setProdPickerIndex(index);
                                      setOpenProdDialog(true);
                                    }}
                                  >
                                    หักเป็นสินค้าอื่น
                                  </MenuItem> */}
                                  {impurityOptions.map((option, i) => (
                                    <MenuItem key={i} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  fullWidth
                                  label="น้ำหนัก"
                                  value={item.weight}
                                  onFocus={() => {
                                    setFocused(null);
                                    setIsEditingNote(false);
                                    setFocusedImpurityIndex(index);
                                    setFocusedImpurityField("weight");
                                    setInputValue(item.weight?.toString() || 0);
                                  }}
                                  InputProps={{
                                    readOnly: true,
                                    sx: {
                                      fontSize: 20,
                                      fontWeight: 700,
                                      bgcolor:
                                        focusedImpurityIndex === index &&
                                        focusedImpurityField === "weight"
                                          ? "action.hover"
                                          : "background.paper",
                                    },
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography color="text.secondary">
                                          กก.
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  }}
                                  disabled={disabled}
                                />

                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedId(null);
                                    removeImpurity(index);
                                  }}
                                  disabled={disabled}
                                >
                                  <BackspaceIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Box>
                  </Grid>
                </Grid>

                {/* น้ำหนัก สุทธิ / รวมราคา */}
                <Grid container>
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      สุทธิ
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 24,
                        color: isNetNegative ? "error.main" : "text.primary",
                      }}
                    >
                      {display2(netWeight)}{" "}
                      <Typography component="span" color="text.secondary">
                        กก.
                      </Typography>
                    </Typography>
                    {isNetNegative && (
                      <Typography variant="caption" color="error.main">
                        สุทธิห้ามติดลบ
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                    >
                      รวมราคา
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 24 }}>
                      {formatNumber(netWeight * (productPrice?.adjPrice || 0))}{" "}
                      <Typography component="span" color="text.secondary">
                        บาท
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            {/* ขวา: หมายเหตุ + Keypad */}
            <Grid
              item
              xs={12}
              md={12}
              lg={3}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ p: 1, width: "100%" }}>
                <Stack spacing={4}>
                  {/* Keypad — ถูก “ล็อก” เมื่อแก้หมายเหตุ */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      opacity: isEditingNote ? 0.5 : 1,
                      pointerEvents: isEditingNote ? "none" : "auto",
                      transition: "opacity 120ms ease",
                    }}
                  >
                    <Keypad
                      value={inputValue}
                      onChange={(val) => {
                        if (isEditingNote) return;
                        setInputValue(val);
                      }}
                      onClear={() => {
                        if (isEditingNote) return;
                        setInputValue(0);
                        if (focused === "weight") setWeight(0);
                        if (focused === "deduct") setDeduct(0);
                        if (focusedImpurityIndex !== null) {
                          const updated = [...impurities];
                          updated[focusedImpurityIndex].weight = 0;
                          setImpurities(updated);
                        }
                      }}
                      onSubmit={(result) => {
                        if (isEditingNote) return;
                        const val = String(result);
                        if (focused === "weight") {
                          setWeight(val);
                          setFocused("deduct");
                          setInputValue(deduct || 0);
                        } else if (focused === "deduct") {
                          setDeduct(val);
                          setFocused("weight");
                          setInputValue(weight || 0);
                        } else if (
                          focusedImpurityField === "weight" &&
                          focusedImpurityIndex !== null
                        ) {
                          const updated = [...impurities];
                          updated[focusedImpurityIndex].weight = val;
                          setImpurities(updated);
                          setInputValue(val || 0);
                        }
                      }}
                    />
                  </Box>

                  {/* ช่องหมายเหตุ */}
                  <TextField
                    label="หมายเหตุ"
                    placeholder="พิมพ์บันทึกเพิ่มเติมที่นี่..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onFocus={() => {
                      // เข้าโหมดแก้หมายเหตุ: ตัดการเชื่อม Keypad
                      setIsEditingNote(true);
                      setFocused(null);
                      setFocusedImpurityIndex(null);
                      setFocusedImpurityField(null);
                    }}
                    onBlur={() => {
                      // ออกจากโหมดแก้หมายเหตุ
                      setIsEditingNote(false);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    multiline
                    minRows={3}
                    sx={{
                      "& textarea": { fontSize: 16 },
                    }}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSummit}
            disabled={!canSubmit}
            sx={{
              borderRadius: 0,
              py: 1.8,
              fontWeight: 700,
              bgcolor: "#1E2630",
              "&:hover": { bgcolor: "#111820" },
            }}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog เลือกสินค้า (สิ่งเจือปนเป็นสินค้า) */}
      <Dialog open={openProdDialog} onClose={() => {}} fullWidth maxWidth="xl">
        <DialogTitle>เลือกสินค้า</DialogTitle>
        <DialogContent sx={{ display: "flex", p: 2 }}>
          {/* Sidebar หมวด */}
          <Paper
            variant="outlined"
            sx={{ width: 240, maxHeight: "70vh", overflowY: "auto", mr: 2 }}
          >
            <Box
              onClick={() => setCatFilter(null)}
              sx={{
                p: 2,
                cursor: "pointer",
                bgcolor: catFilter === null ? "action.selected" : "transparent",
              }}
            >
              ทั้งหมด
            </Box>
            {[
              ...new Map(categories.map((cat) => [cat.name, cat])).values(),
            ].map((cat) => (
              <Box
                key={cat.id}
                onClick={() => setCatFilter(cat.name)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  bgcolor:
                    catFilter === cat.name ? "action.selected" : "transparent",
                }}
              >
                {cat.name}
              </Box>
            ))}
          </Paper>

          {/* Main เลือกสินค้า */}
          <Box flex={1} display="flex" flexDirection="column">
            <TextField
              fullWidth
              placeholder="ค้นหาชื่อสินค้า..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box flex={1} overflow="auto">
              <Grid container spacing={2}>
                {filteredProds.map((p) => {
                  const isSel = selectedId === p.id;
                  return (
                    <Grid item xs={6} sm={4} md={3} key={p.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          borderColor: isSel ? "success.main" : undefined,
                          bgcolor: isSel
                            ? theme.palette.grey[300]
                            : alpha(
                                catColorMap[p.category?.name] || grey[200],
                                0.2
                              ),
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            setSelectedId((prev) =>
                              prev === p.id ? null : p.id
                            )
                          }
                        >
                          <CardContent>
                            <Typography noWrap>{p.title}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {p.subCategory?.name}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Chip
            label={`เลือกแล้ว ${selectedId ? 1 : 0} ชิ้น`}
            color={selectedId ? "success" : "default"}
          />
          <Button size="large" onClick={() => setSelectedId(null)}>
            ล้าง
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              const prod = products.find((p) => p.id === selectedId);
              if (!prod) return;

              setImpurities((prev) => {
                const updated = [...prev];
                if (prodPickerIndex != null && updated[prodPickerIndex]) {
                  // แทนค่าบรรทัดที่เลือกไว้
                  updated[prodPickerIndex] = {
                    ...updated[prodPickerIndex],
                    type: "__deduct_product__",
                    productId: prod.id,
                    productName: prod.title,
                    weight: updated[prodPickerIndex].weight ?? 0,
                  };
                } else {
                  // fallback ถ้าไม่รู้ index ก็สร้างใหม่
                  updated.push({
                    productId: prod.id,
                    productName: prod.title,
                    weight: 0,
                    type: "__deduct_product__",
                  });
                }
                return updated;
              });

              setSelectedId(null);
              setProdPickerIndex(null); // ล้าง index
              setOpenProdDialog(false);
            }}
            disabled={!selectedId}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog แก้ไขสินค้า (ตัวหลัก) */}
      <ProductSelectDialog
        open={openMainProdDialog}
        onClose={() => setOpenMainProdDialog(false)}
        onConfirm={(prod) => {
          setMainProduct({
            id: prod.id,
            title: prod.title,
            categoryId: prod.category?.id,
          });
          setOpenMainProdDialog(false);
        }}
        products={products}
        categories={categories}
        initialSelectedId={mainProduct?.id || selectedValue?.id}
      />

      {/* Dialog เลือกราคากลุ่ม */}
      <PriceSelectionDialog
        open={openPriceDialog}
        onClose={() => setOpenPriceDialog(false)}
        onSelect={handleSelectPrice}
        categoryId={mainProduct?.categoryId || selectedValue?.categoryId}
        productPrice={productPrice}
      />
    </>
  );
};

export default SimpleDialog;
